import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../../api/axios";
import ProjectDialog from "../../dashboard/projects/dialog";
import { PMEntityPicker } from "./PMEntityPicker";
import { InitialsAvatar, PICKER_PAGE_SIZE, PickerTag, joinParts, rowsFrom } from "./pickerParts";

export const projectToItem = (row) =>
  row?.id == null
    ? null
    : {
        id: row.id,
        primary: row.displayTitle ?? row.name ?? `#${row.id}`,
        secondary: joinParts(
          row.customer?.displayTitle ?? row.customer?.name,
          row.parent_project ? row.parent_project.displayTitle ?? row.parent_project.name : null
        ),
        leading: <InitialsAvatar text={row.displayTitle ?? row.name} square />,
        tag: <PickerTag>{row.project_number}</PickerTag>,
        object: row,
      };

export async function fetchProjectOptions({ query, filter, customerId }) {
  const byCustomer = filter === "customer" && customerId != null;
  const response = await axios.get("/projects/", {
    params: {
      query: query || undefined,
      page: 1,
      per_page: PICKER_PAGE_SIZE,
      sortBy: "created_at",
      sort: "desc",
      fields: "*",
      offer_scope: "all",
      // The API lists active projects unless `active` is given.
      active: filter === "inactive" ? false : undefined,
      filters: byCustomer ? { 0: { name: "customer", value: String(customerId) } } : undefined,
    },
  });
  return rowsFrom(response).map(projectToItem);
}

/**
 * Project picker (new design). Filters: the selected customer's projects,
 * all active projects, or finished (inactive) ones. Emits the legacy
 * ProjectSelector contract: `onParentObjectChange(row)` and
 * `onChange({ target: { name, value, object, data } })`.
 */
export function ProjectPicker({
  name = "project_id",
  label,
  value,
  customerId,
  onChange,
  onParentObjectChange,
  setFieldValue,
  disabled = false,
  error,
  required = false,
}) {
  const { t } = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);
  const selectedId = value?.id ?? null;
  const hasCustomer = customerId != null && customerId !== "";

  const filters = useMemo(
    () => [
      ...(hasCustomer ? [{ key: "customer", label: t("pickers.project.filterCustomer") }] : []),
      { key: "active", label: t("pickers.project.filterActive") },
      { key: "inactive", label: t("pickers.project.filterInactive") },
    ],
    [hasCustomer, t]
  );

  const loadOptions = useCallback(
    ({ query, filter }) => fetchProjectOptions({ query, filter, customerId }),
    [customerId]
  );

  const select = (item) => {
    const row = item?.object ?? null;
    const id = row?.id ?? null;
    setFieldValue?.(name, id);
    onParentObjectChange?.(row);
    onChange?.({ target: { name, value: id, object: row, data: item } });
  };

  return (
    <>
      <PMEntityPicker
        label={label ?? t("Project")}
        labelAction={selectedId != null ? { action: () => setDetailOpen(true) } : null}
        required={required}
        value={projectToItem(value)}
        placeholder={t("pickers.project.placeholder")}
        searchPlaceholder={t("pickers.project.search")}
        disabled={disabled}
        error={error}
        loadOptions={loadOptions}
        filters={filters}
        defaultFilter={hasCustomer ? "customer" : "active"}
        pageSize={PICKER_PAGE_SIZE}
        onSelect={select}
        emptyText={t("pickers.project.empty")}
      />
      {detailOpen && selectedId != null && (
        <ProjectDialog
          open
          onClose={() => setDetailOpen(false)}
          readOnly
          selected={{ id: selectedId }}
        />
      )}
    </>
  );
}
